import { useEffect, useRef } from 'react';

import { type ContentTiedToExpiredOrder, TransactionProfile } from 'pages/access_codes/components/TransactionProfile/TransactionProfile';
import { AssignedAccessCode, LicenseReturnable, Transaction, TransactionApi, TransactionDetails } from 'apis/TransactionApi';
import { TransactionOrderExpiredDialog } from 'components/TransactionOrderExpiredDialog/TransactionOrderExpiredDialog';
import { TransactionDetailsContext, useTransactionDetails } from 'pages/access_codes/context';
import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import { duplicateTransaction } from 'pages/access_codes/duplicateTransaction';
import { getFormattedDate, mapLicensesToGetOrderDetails } from 'utils';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getMostRestrictedLicenseDeliverable } from 'utils/objects';
import GMetrixBanner from 'components/GMetrixBanner/GMetrixBanner';
import { GroupDetailsContext } from 'pages/groups/context';
import { FormattedMessage, useIntl } from 'react-intl';
import { helpContent } from 'pages/access_codes/help';
import { useNotification } from 'app/notifications';
import { PageWithHeader } from 'components/layout';
import { KendoIntlProvider } from 'app/culture';
import { useSuperState } from 'hooks';
import { useApp } from 'app/context';

import 'styles/global-styles.scss';
import 'styles/kendo-default-theme-with-overrides.scss';
import 'pages/access_codes/styles/main.scss';

// Picks the access code to show by default: the only code, or the first active one.
const getDefaultAccessCode = (accessCodes: AssignedAccessCode[] | null | undefined | void): string | undefined => {

    if (accessCodes && accessCodes.length === 1) return accessCodes[0].AccessCodeTitle;
    if (accessCodes && accessCodes.length > 1) return accessCodes.find(code => code.Active)?.AccessCodeTitle;
    return undefined;

};

export const AccessCodeDetail = () => {

    const { id } = useParams();
    const transactionId = Number(id);
    const navigate = useNavigate();
    const location = useLocation();

    const [state, setState] = useSuperState({
        globalLicenses: null as LicenseReturnable | null,
        inactiveOrdersWContentOnOpenTransaction: [] as ContentTiedToExpiredOrder[],
        loading: true,
        transactionOrderExpiredDialogShow: false
    });

    const { inactiveOrdersWContentOnOpenTransaction, transactionOrderExpiredDialogShow } = state;

    const { open, pageContext, setOpenTabIndex, setSubTabIndex, updateById } = useTransactionDetails();
    const { locale } = useIntl();
    const intl = useIntl();
    const { formatMessage } = intl;
    const { catchWithToast, createToast } = useNotification();
    const { highestRole, isSystemMode, selectedPartner } = useApp();

    const gmetrixBannerRef = useRef<any>();

    // PHASE 2: background-load the data the secondary tabs need (content, students, inactive
    // orders) after the Details tab has already rendered, then merge it into the open tab.
    const loadSecondaryTransactionData = async (
        dataItem: Transaction,
        partnerId: number | null,
        transactionDetails: TransactionDetails,
        licenses: LicenseReturnable
    ) => {

        try {

            const [assignedContentResult, inactiveOrdersResult, students] = await Promise.all([
                TransactionApi.GetLimitedContentByTransactionId(dataItem.Id, partnerId).catch(catchWithToast(formatMessage({ id: 'failed_to_load_assigned_content.3392953e', defaultMessage: 'Failed to load assigned content.' }))),
                TransactionApi.GetInactiveOrdersByTransactionId(dataItem.Id, 1, partnerId).catch(catchWithToast(formatMessage({ id: 'failed_to_load_inactive_orders.2ab9bd25', defaultMessage: 'Failed to load inactive orders.' }))),
                TransactionApi.GetStudentsByTransactionId(dataItem.Id, 1, partnerId).catch(catchWithToast(formatMessage({ id: 'failed_to_load_student_list.bb95052d', defaultMessage: 'Failed to load student list.' })))
            ]);

            let assignedContent = assignedContentResult;
            let inactiveOrders = inactiveOrdersResult;

            if (inactiveOrders && licenses) {

                inactiveOrders = mapLicensesToGetOrderDetails(
                    licenses.allLicenseHistory,
                    [...inactiveOrders.map(order => order.OrderId)]
                );

            }

            if (assignedContent && assignedContent.length > 0) {

                assignedContent = assignedContent.map(content => {

                    if (content.ExpirationDate && content.StartDate && content.ExpirationDateString) {

                        content.ExpirationDate = new Date(content.ExpirationDate);
                        content.ExpirationDateString = getFormattedDate(new Date(content.ExpirationDateString));
                        content.StartDate = new Date(content.StartDate);
                        content.StartDateString = getFormattedDate(new Date(content.StartDateString));

                    }
                    return content;

                });

            }

            if (transactionDetails.HasXED || transactionDetails.HasBulbAccess || transactionDetails.HasCloud) {

                // skip the error message for now
            } else if (transactionDetails.HasBulbAccess === false && (assignedContent?.length === 0 || !assignedContent)) {

                createToast(formatMessage({ id: 'unable_to_find_assigned_content_please_s.7b69f3e2', defaultMessage: 'Unable to find assigned content, please select it again' }), 'danger');

            }

            updateById(dataItem.Id, {
                content: assignedContent ?? [],
                contentOriginal: assignedContent ?? [],
                inactiveOrders: inactiveOrders ?? [],
                mostRestrictiveLicense: getMostRestrictedLicenseDeliverable(licenses.licenses, assignedContent ?? []),
                secondaryLoading: false,
                students: students ?? []
            });

        } catch (e) {

            console.error(e);
            updateById(dataItem.Id, { secondaryLoading: false });

        }

    };

    const loadTransaction = async () => {

        try {

            setState({ loading: true });
            gmetrixBannerRef.current?.removeBanner();

            // Fetch the access code (and available test content) first. Licenses are loaded afterwards
            // so they can be scoped to the access code's owning partner rather than the current user/partner.
            // Get resolves the record by id (even when archived or owned by another partner).
            const [transactionDetails, testContentObj] = await Promise.all([
                TransactionApi.Get(transactionId).catch(error => {

                    catchWithToast(formatMessage({ id: 'failed_to_load_access_code_details.ed1f3281', defaultMessage: 'Failed to load access code details.' }))(error);
                    return undefined;

                }),
                TransactionApi.GetAllContentForTransactions(true).catch(catchWithToast(formatMessage({ id: 'failed_to_load_available_content.ba08850a', defaultMessage: 'Failed to load available content.' })))
            ]);

            if (!transactionDetails) {

                // Not found / failed to load — fall back to the lookup screen and surface the error.
                createToast(formatMessage({ id: 'failed_to_load_access_code_details_pleas.f0e7741a', defaultMessage: 'Failed to load access code details. Please try again.' }), 'danger');
                navigate('/access-codes');
                return;

            }

            // Only a SysAdmin acting in their own system context (not managing another partner) scopes
            // by the access code's true owner. In that case resolve the owner from the paged list (which
            // carries the true owner) so the partner is labelled/scoped correctly — mirroring the
            // row-click path. Anyone managing a partner (e.g. a Testing Center) or logged in as one
            // scopes by the currently managed partner instead.
            const isSysAdminInSystemMode = highestRole?.id === 1 && isSystemMode;

            if (isSysAdminInSystemMode) {

                const partnerSearchTerm = transactionDetails.Identifier || transactionDetails.Codes || transactionDetails.Name || '';
                const pagedTransactions = await TransactionApi.GetAllPaged(1, 25, '', '', partnerSearchTerm)
                    .catch(catchWithToast(formatMessage({ id: 'failed_to_load_access_code_partner_data.daafe556', defaultMessage: 'Failed to load access code partner data.' })));
                const ownerPartner = pagedTransactions?.Items?.find(item => item.Id === transactionDetails.Id)?.Partner
                    ?? pagedTransactions?.Items?.[0]?.Partner;
                if (ownerPartner) {

                    transactionDetails.Partner = ownerPartner;
                    transactionDetails.PartnerId = ownerPartner.Id;

                }

            } else if (selectedPartner) {

                transactionDetails.PartnerId = selectedPartner.id;

            }

            const transactionPartnerId = transactionDetails.PartnerId ?? null;

            // Scope licenses to the resolved partner so the license data reflects this access code,
            // not necessarily the current user/partner.
            const licenseObj = await TransactionApi.GetAllLicenses(transactionPartnerId)
                .catch(catchWithToast(formatMessage({ id: 'failed_to_load_license_data.ccdc93b9', defaultMessage: 'Failed to load license data.' })));
            if (!licenseObj) {

                // Not found / failed to load — fall back to the lookup screen and surface the error.
                createToast(formatMessage({ id: 'failed_to_load_access_code_details_pleas.f0e7741a', defaultMessage: 'Failed to load access code details. Please try again.' }), 'danger');
                navigate('/access-codes');
                return;

            }

            setState({ globalLicenses: licenseObj });

            const accessCodes = await TransactionApi.GetAccessCodesByTransactionId(transactionDetails.Id, transactionPartnerId)
                .catch(catchWithToast(formatMessage({ id: 'failed_to_load_access_codes.242fe9e5', defaultMessage: 'Failed to load access codes.' })));

            const defaultAccessCode = getDefaultAccessCode(accessCodes);

            const mappedDetails = {
                ...transactionDetails,
                ExpirationDate: transactionDetails.ExpirationDateString
                    ? new Date(transactionDetails.ExpirationDateString)
                    : undefined,
                ExpirationDateString: transactionDetails.ExpirationDateString ? getFormattedDate(
                    new Date(transactionDetails.ExpirationDateString)
                ) : null,
                StartDate: transactionDetails.StartDateString
                    ? new Date(transactionDetails.StartDateString)
                    : undefined,
                StartDateString: transactionDetails.StartDateString ? getFormattedDate(
                    new Date(transactionDetails.StartDateString)
                ) : null
            };

            open({
                Data: {
                    accessCodes: accessCodes ?? [],
                    defaultAccessCode,
                    licenseHistory: licenseObj.allLicenseHistory,
                    licenses: licenseObj.licenses,
                    secondaryLoading: true,
                    testContent: testContentObj?.contentFlatList ?? [],
                    transactionDetails: { ...mappedDetails },
                    transactionDetailsOriginal: { ...mappedDetails }
                },
                HasChanges: false,
                Id: transactionDetails.Id,
                Page: 'Transactions',
                Title: transactionDetails.Name
            });

            setState({ loading: false });

            // PHASE 2: background-load the secondary tab data and merge it in.
            loadSecondaryTransactionData(transactionDetails, transactionPartnerId, transactionDetails, licenseObj);

        } catch (e) {

            console.error(e);

            // Something went wrong loading the access code — fall back to the lookup screen.
            createToast(formatMessage({ id: 'failed_to_load_access_code_details_pleas.f0e7741a', defaultMessage: 'Failed to load access code details. Please try again.' }), 'danger');
            navigate('/access-codes');

        }

    };

    const handleDuplicateTransaction = async (dataItem: Transaction, setDuplicateProcess?: any) => {

        try {

            const newId = await duplicateTransaction(dataItem, intl, createToast);
            if (newId) {

                navigate('/access-codes/' + newId);

            }

        } finally {

            if (setDuplicateProcess) {

                setDuplicateProcess(false);

            }

        }

    };

    // Reloads the current access code after an order/expiration change (used by the expired-order dialog).
    const reloadAccessCode = async (dataItem: Transaction) => {

        navigate('/access-codes/' + dataItem.Id);
        loadTransaction();

    };

    useEffect(() => {

        loadTransaction();

    }, [transactionId]);

    // Honor a sub-tab hint passed via navigation state (e.g. "View Codes" jumps to the Codes tab).
    useEffect(() => {

        const subTab = (location.state as { subTab?: number } | null)?.subTab;
        if (!state.loading && pageContext && subTab !== undefined) {

            setOpenTabIndex?.(1);
            setSubTabIndex?.([subTab]);

        }

    }, [state.loading, pageContext?.Id]);

    // Surface a banner when the open access code has content tied to an expired order.
    useEffect(() => {

        if (inactiveOrdersWContentOnOpenTransaction.length > 0) {

            gmetrixBannerRef.current?.createBanner(
                <span className="d-inline-flex align-items-center gap-2">
                    <FormattedMessage defaultMessage="There is an issue with one or more orders linked to this Access Code." id="there_is_an_issue_with_one_or_more_order.5d9d7b2e" />
                    <GMetrixButton
                        onClick={() => setState({ transactionOrderExpiredDialogShow: true })}
                        size="sm"
                        variant="danger"
                    >
                        <FormattedMessage defaultMessage="Resolve License Issue" id="resolve_license_issue.393fd474" />
                    </GMetrixButton>
                </span>
                , 'danger', true);

        } else {

            gmetrixBannerRef.current?.removeBanner();

        }

    }, [inactiveOrdersWContentOnOpenTransaction]);

    return (
        <KendoIntlProvider>
            <LocalizationProvider language={locale}>
                <IntlProvider locale={locale}>
                    <div className={'access-code-detail'}>

                        <GMetrixBanner ref={gmetrixBannerRef} />

                        <PageWithHeader
                            headerConfig={{
                                helpContent: helpContent,

                                // While a new access code loads, pageContext still holds the previously
                                // opened tab. Show the name passed from the lookup page instantly and
                                // swap to the loaded title once pageContext matches the current route id.
                                title: pageContext?.Id === transactionId
                                    ? (pageContext?.Title ?? '')
                                    : ((location.state as { title?: string } | null)?.title ?? ''),
                                trail: [{ label: formatMessage({ id: 'access_codes.3114ae04', defaultMessage: 'Access Codes' }), to: '/access-codes' }]
                            }}
                        >

                            {state.loading || !pageContext ?
                                (
                                    <div className={'d-flex align-items-center justify-content-center'} style={{ height: 300 }}>
                                        <GMetrixLoader />
                                    </div>
                                )
                                :
                                (
                                    <TransactionProfile
                                        setInactiveOrdersWContentOnOpenTransaction={
                                            val => setState({ inactiveOrdersWContentOnOpenTransaction: typeof val === 'function' ? val(inactiveOrdersWContentOnOpenTransaction) : val })
                                        }
                                        contentTiedToInactiveOrder={inactiveOrdersWContentOnOpenTransaction.length > 0}
                                        handleDuplicateTransaction={handleDuplicateTransaction}
                                        onDetailsItemClick={async () => undefined}
                                        {...pageContext}
                                    />
                                )
                            }

                        </PageWithHeader>

                        <TransactionOrderExpiredDialog
                            context="Access Code"
                            contextAccessCode={TransactionDetailsContext}
                            contextClassroom={GroupDetailsContext}
                            inactiveOrdersWContentOnOpenTransaction={inactiveOrdersWContentOnOpenTransaction}
                            reloadAccessCode={reloadAccessCode}
                            setShow={val => setState({ transactionOrderExpiredDialogShow: val })}
                            show={transactionOrderExpiredDialogShow}
                        />
                    </div>
                </IntlProvider>
            </LocalizationProvider>
        </KendoIntlProvider>
    );

};
